/**
 * Debug Logging Utility
 *
 * Provides consistent debug logging across the application.
 * Automatically enabled in Rails development environment.
 *
 * Usage in controllers:
 *   import { debug } from '../utils/debug'
 *
 *   // In your controller
 *   debug('cart', 'Product Added', { sku: 'SS-01', price: 15 })
 *   // Outputs: [cart] Product Added {sku: 'SS-01', price: 15}
 *
 * Usage with debug.log (simple):
 *   debug.log('Something happened', data)
 *   // Outputs: [debug] Something happened {...}
 *
 * Manual toggle:
 *   debug.enable()   // Force enable
 *   debug.disable()  // Force disable
 *   debug.isEnabled  // Check current state
 */

class Debug {
  constructor() {
    // Start with null - will be lazily determined on first check
    this._enabled = null
  }

  get isEnabled() {
    // Lazy check - determine on first access when window.env should be set
    if (this._enabled === null) {
      this._enabled = typeof window !== 'undefined' && window.env === 'development'
    }
    return this._enabled
  }

  enable() {
    this._enabled = true
    console.debug('[debug] Debug logging enabled')
  }

  disable() {
    this._enabled = false
  }

  /**
   * Log with a namespace prefix
   * Uses console.debug() so logs appear at "Verbose" level in Chrome DevTools
   * (hidden by default, enable via Console > Log Levels > Verbose)
   * @param {string} namespace - The prefix (e.g., 'cart', 'analytics', 'search')
   * @param  {...any} args - Arguments to log
   */
  log(namespace, ...args) {
    if (!this.isEnabled) return
    console.debug(`[${namespace}]`, ...args)
  }

  /**
   * Warn with a namespace prefix
   */
  warn(namespace, ...args) {
    if (!this.isEnabled) return
    console.warn(`[${namespace}]`, ...args)
  }

  /**
   * Error logging (always logs, regardless of debug mode)
   */
  error(namespace, ...args) {
    console.error(`[${namespace}]`, ...args)
  }

  /**
   * Group logs together (collapsible in DevTools)
   */
  group(namespace, label) {
    if (!this.isEnabled) return
    console.groupCollapsed(`[${namespace}] ${label}`)
  }

  groupEnd() {
    if (!this.isEnabled) return
    console.groupEnd()
  }

  /**
   * Log with timing info
   */
  time(namespace, label) {
    if (!this.isEnabled) return
    console.time(`[${namespace}] ${label}`)
  }

  timeEnd(namespace, label) {
    if (!this.isEnabled) return
    console.timeEnd(`[${namespace}] ${label}`)
  }
}

// Singleton instance
const debugInstance = new Debug()

// Convenience function for simple logging
// Usage: debug('namespace', 'message', data)
function debug(namespace, ...args) {
  debugInstance.log(namespace, ...args)
}

// Attach methods to the function for flexibility
debug.log = (namespace, ...args) => debugInstance.log(namespace, ...args)
debug.warn = (namespace, ...args) => debugInstance.warn(namespace, ...args)
debug.error = (namespace, ...args) => debugInstance.error(namespace, ...args)
debug.group = (namespace, label) => debugInstance.group(namespace, label)
debug.groupEnd = () => debugInstance.groupEnd()
debug.time = (namespace, label) => debugInstance.time(namespace, label)
debug.timeEnd = (namespace, label) => debugInstance.timeEnd(namespace, label)
debug.enable = () => debugInstance.enable()
debug.disable = () => debugInstance.disable()

Object.defineProperty(debug, 'isEnabled', {
  get: () => debugInstance.isEnabled
})

// Attach to window for console access
if (typeof window !== 'undefined') {
  window.debug = debug
}

export { debug, Debug }
export default debug

